import React, { useMemo, useState } from 'react';
import { Stethoscope, AlertTriangle, Camera, Ban, CalendarClock, ShieldAlert, CheckCircle2, Save } from 'lucide-react';
import Button from '../../../ui-kit/Button';
import useComplaintStore from '../store/complaintStore';
import { fillTemplate } from '../constants';
import { isDoctorIssue, productComplaintSummary, batchComplaintSummary, doctorWarnings } from '../logic/doctorCase';
import { TicketService } from '../services/ticketService';
import QuickCallButton from '../../customers/ui/QuickCallButton';

const inputCls = 'w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm';
const fmt = (ts) => ts ? new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const DoctorCaseSection = ({ ticket, currentUser }) => {
    const { tickets } = useComplaintStore();
    const dc = ticket.doctorCase || {};
    const [form, setForm] = useState({
        skinType: dc.skinType || '', productUsed: dc.productUsed || (ticket.productNames || []).join(', '),
        batchNumber: dc.batchNumber || '', purchaseDate: dc.purchaseDate || '', deliveredDate: dc.deliveredDate || '',
        usageDays: dc.usageDays || '', photosUploaded: !!dc.photosUploaded, doctorAssigned: dc.doctorAssigned || '',
        doctorAdvice: dc.doctorAdvice || '', discontinueSuggested: !!dc.discontinueSuggested, followUpAfterDays: dc.followUpAfterDays || '',
    });
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
    const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { alert(e.message); } finally { setBusy(false); } };

    // ALL hooks must run before the conditional return (React hook-order rule).
    const others = useMemo(() => tickets.filter(t => t.id !== ticket.id), [tickets, ticket.id]);
    const prodSummary = useMemo(() => productComplaintSummary(others, ticket.productNames || []), [others, ticket.productNames]);
    const batchSummary = useMemo(() => batchComplaintSummary(others, form.batchNumber), [others, form.batchNumber]);
    const warnings = useMemo(
        () => doctorWarnings({ ...ticket, doctorCase: { ...dc, batchNumber: form.batchNumber, photosUploaded: form.photosUploaded } }, tickets),
        [ticket, dc, form.batchNumber, form.photosUploaded, tickets]
    );

    // Show ONLY for genuine doctor cases: doctor-type issue, doctor department,
    // doctor-review status, or an existing doctor case record.
    const isDoctorCase = isDoctorIssue(ticket.issueType) || ticket.assignedDepartment === 'doctor'
        || ticket.status === 'doctor_review' || !!ticket.doctorCase;
    if (!isDoctorCase) return null;

    const saveFields = () => run(() => TicketService.updateDoctorCase(ticket.id, {
        skinType: form.skinType, productUsed: form.productUsed, batchNumber: form.batchNumber,
        purchaseDate: form.purchaseDate, deliveredDate: form.deliveredDate, usageDays: form.usageDays,
        photosUploaded: form.photosUploaded, doctorAssigned: form.doctorAssigned,
        status: ticket.doctorCase?.status || 'doctor_review',
    }));

    return (
        <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-bold uppercase tracking-wide text-purple-500 flex items-center gap-1">
                    <Stethoscope size={14} /> Doctor Case
                </h4>
                {ticket.mobile && (
                    <div className="flex items-center gap-1">
                        {[
                            { ct: 'doctor_followup', label: 'Doctor Call' },
                            { ct: 'photo_request', label: 'Ask Photo' },
                            { ct: 'usage_correction', label: 'Usage' },
                            { ct: 'discontinue_followup', label: 'Discontinue' },
                        ].map(b => (
                            <QuickCallButton key={b.ct} mobile={ticket.mobile} customerName={ticket.customerName}
                                ticketId={ticket.id} orderId={ticket.orderId || ''} sourceModule="doctor" callType={b.ct}
                                label={<span className="text-[10px] font-semibold">{b.label}</span>}
                                className="px-2 py-1 rounded border border-purple-200 text-purple-700 hover:bg-purple-50" title={`Call & log: ${b.label}`} />
                        ))}
                    </div>
                )}
            </div>

            {warnings.length > 0 && (
                <div className="space-y-1 mb-3">
                    {warnings.map((w, i) => (
                        <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg px-2 py-1.5">
                            <AlertTriangle size={13} className="shrink-0" /> {w}
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-slate-500">Skin type
                    <input className={inputCls} value={form.skinType} onChange={e => set('skinType', e.target.value)} /></label>
                <label className="text-xs text-slate-500">Product used
                    <input className={inputCls} value={form.productUsed} onChange={e => set('productUsed', e.target.value)} /></label>
                <label className="text-xs text-slate-500">Batch number
                    <input className={inputCls} value={form.batchNumber} onChange={e => set('batchNumber', e.target.value)} /></label>
                <label className="text-xs text-slate-500">Usage days
                    <input className={inputCls} value={form.usageDays} onChange={e => set('usageDays', e.target.value)} /></label>
                <label className="text-xs text-slate-500">Purchase date
                    <input type="date" className={inputCls} value={form.purchaseDate} onChange={e => set('purchaseDate', e.target.value)} /></label>
                <label className="text-xs text-slate-500">Delivered date
                    <input type="date" className={inputCls} value={form.deliveredDate} onChange={e => set('deliveredDate', e.target.value)} /></label>
                <label className="text-xs text-slate-500">Doctor assigned
                    <input className={inputCls} value={form.doctorAssigned} onChange={e => set('doctorAssigned', e.target.value)} /></label>
                <label className="text-xs text-slate-500 flex items-center gap-2 mt-5">
                    <input type="checkbox" checked={form.photosUploaded} onChange={e => set('photosUploaded', e.target.checked)} /> Photos uploaded
                </label>
            </div>

            <label className="text-xs text-slate-500 block mt-2">Doctor advice
                <textarea className={inputCls} rows={2} value={form.doctorAdvice} onChange={e => set('doctorAdvice', e.target.value)} /></label>
            <div className="flex items-center gap-3 mt-2">
                <label className="text-xs text-slate-500 flex items-center gap-2">
                    <input type="checkbox" checked={form.discontinueSuggested} onChange={e => set('discontinueSuggested', e.target.checked)} /> Discontinue product
                </label>
                <label className="text-xs text-slate-500 flex items-center gap-1">
                    Follow-up after
                    <input className="w-16 px-2 py-1 border border-slate-200 rounded text-sm" value={form.followUpAfterDays} onChange={e => set('followUpAfterDays', e.target.value)} /> days
                </label>
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
                <Button size="sm" disabled={busy} onClick={saveFields}><Save size={14} /> Save case</Button>
                <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => run(async () => {
                        await TicketService.updateDoctorCase(ticket.id, { status: 'waiting_customer' });
                        await TicketService.changeStatus(ticket.id, 'waiting_customer', currentUser, { ticketNumber: ticket.ticketNumber, mobile: ticket.mobile, issueType: ticket.issueType });
                        await TicketService.addPublicUpdate(ticket.id, fillTemplate('waiting_customer', ticket.ticketNumber), { status: 'waiting_customer', channel: 'whatsapp', sentToCustomer: true });
                    })}>
                    <Camera size={14} /> Ask for photo
                </Button>
                <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => run(() => TicketService.setDoctorAdvice(ticket, {
                        doctorAdvice: form.doctorAdvice, discontinueSuggested: form.discontinueSuggested,
                        followUpAfterDays: form.followUpAfterDays, doctorAssigned: form.doctorAssigned,
                    }, currentUser))}>
                    <Ban size={14} /> Save advice {form.followUpAfterDays ? '+ follow-up' : ''}
                </Button>
                <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => run(async () => {
                        await TicketService.addInternalNote(ticket.id, 'Escalated to product quality team', currentUser, 'doctor');
                        await TicketService.escalate(ticket.id, { fromDepartment: 'doctor', toDepartment: 'manager', reason: 'Product quality review' }, currentUser, { ticketNumber: ticket.ticketNumber, mobile: ticket.mobile });
                    })}>
                    <ShieldAlert size={14} /> Escalate to Quality
                </Button>
                <Button size="sm" variant="outline" disabled={busy}
                    onClick={() => run(async () => {
                        await TicketService.updateDoctorCase(ticket.id, { status: 'closed' });
                        await TicketService.changeStatus(ticket.id, 'resolved', currentUser, { ticketNumber: ticket.ticketNumber, mobile: ticket.mobile, issueType: ticket.issueType });
                    })}>
                    <CheckCircle2 size={14} /> Close doctor review
                </Button>
            </div>

            {/* Product / batch complaint summary */}
            <div className="grid grid-cols-2 gap-2 mt-4">
                <div className="bg-slate-50 rounded-lg p-2 text-xs">
                    <div className="font-bold text-slate-600 mb-1">This product</div>
                    <div>Total complaints: <b>{prodSummary.total + ((ticket.productNames || []).length ? 1 : 0)}</b></div>
                    <div>Last: {fmt(prodSummary.lastComplaintAt)}</div>
                    {prodSummary.commonIssues.slice(0, 3).map(c => <div key={c.name} className="text-slate-500">• {c.name} ({c.count})</div>)}
                </div>
                <div className="bg-slate-50 rounded-lg p-2 text-xs">
                    <div className="font-bold text-slate-600 mb-1">This batch {form.batchNumber ? `(${form.batchNumber})` : ''}</div>
                    <div>Total complaints: <b>{batchSummary.total + (form.batchNumber ? 1 : 0)}</b></div>
                    <div>Last: {fmt(batchSummary.lastComplaintAt)}</div>
                    {batchSummary.commonIssues.slice(0, 3).map(c => <div key={c.name} className="text-slate-500">• {c.name} ({c.count})</div>)}
                </div>
            </div>
        </div>
    );
};

export default DoctorCaseSection;
